/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useInfiniteQuery,
  useQuery,
  UseQueryOptions
} from '@tanstack/react-query';
import {merchantApiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {
  getOrganisationsResponseTransformer,
  getOrganisationRequestsResponseTransformer,
  transformOrganisationForTable
} from '#utils/response-transformers/organisations';
import type {TransformedOrganisationsResponse} from '#types/organisation';
import {STALE_TIME, GC_TIME} from '#constants';

// Hook for fetching merchant organisations
export function useMerchantOrganisation(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.merchant.all, 'organisations', params],
    queryFn: async () => {
      const response = await merchantApiClient.getMerchantOrganisation(params);
      const transformed = getOrganisationsResponseTransformer(response as any);
      return {
        data: transformed.organisations.map(transformOrganisationForTable),
        pagination: transformed.pagination
      };
    },
    ...options
  });
}

// Hook for fetching merchant suggestions
export function useMerchantSuggestions(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.merchant.all, 'suggestions', params],
    queryFn: () => merchantApiClient.getMerchantSuggestions(params),
    staleTime: STALE_TIME.MERCHANT_FEED,
    gcTime: GC_TIME.MERCHANT_FEED,
    ...options
  });
}

// Hook for fetching merchant organisation request by ID
export function useMerchantOrganisationRequestById(
  id: string | number,
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.merchant.all, 'organisation-request', id],
    queryFn: () => merchantApiClient.getMerchantOrganisationRequestById(id),
    staleTime: STALE_TIME.MERCHANT,
    gcTime: GC_TIME.MERCHANT,
    enabled: !!id,
    ...options
  });
}

// Hook for searching merchant organisations (uses /merchant/search-filter/organisation)
export function useMerchantOrganisationSearch(
  params: {search?: string; limit?: number; pageNumber?: number},
  options?: Omit<
    UseQueryOptions<{
      data: TransformedOrganisationsResponse['organisations'];
      pagination: TransformedOrganisationsResponse['pagination'];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: [...queryKeys.merchant.all, 'organisation-search', params],
    queryFn: async () => {
      const response = await merchantApiClient.getAllOrganisations(params);
      const transformed = getOrganisationsResponseTransformer(response as any);

      return {
        data: transformed.organisations,
        pagination: transformed.pagination
      };
    },
    staleTime: STALE_TIME.MERCHANT_FEED,
    gcTime: GC_TIME.MERCHANT_FEED,
    ...options
  });
}

// Hook for searching merchant organisations (uses /merchant/search-filter/organisation)
export function useInifiniteMerchantOrganisations(
  params: {limit?: number},
  options?: any
) {
  return useInfiniteQuery({
    queryKey: [
      ...queryKeys.merchant.all,
      'organisation-search',
      'infinite',
      params
    ],
    queryFn: async ({pageParam}) => {
      const response = await merchantApiClient.getAllOrganisations({
        ...params,
        pageNumber: pageParam
      });
      const transformed = getOrganisationsResponseTransformer(response as any);

      return {
        data: transformed.organisations,
        pagination: transformed.pagination
      };
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (lastPage.pagination.hasMore)
        return lastPage.pagination.currentPage + 1;
      return lastPage.pagination.currentPage;
    },
    staleTime: STALE_TIME.MERCHANT_FEED,
    gcTime: GC_TIME.MERCHANT_FEED,
    ...options
  });
}

// Hook for fetching merchant organisation requests
export function useMerchantOrganisationRequests(
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.merchant.all, 'organisation-requests', params],
    queryFn: async () => {
      const response =
        await merchantApiClient.getMerchantOrganisationRequests(params);
      const transformed = getOrganisationRequestsResponseTransformer(
        response as any
      );
      return {
        data: transformed.requests.map(transformOrganisationForTable),
        pagination: transformed.pagination
      };
    },
    staleTime: STALE_TIME.MERCHANT,
    gcTime: GC_TIME.MERCHANT,
    ...options
  });
}

// Hook for fetching merchant organisation by ID
export function useMerchantOrganisationById(
  id: string | number,
  params: Record<string, any> = {},
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.merchant.all, 'organisation', id, params],
    queryFn: () => merchantApiClient.getMerchantOrganisationById(id, params),
    staleTime: STALE_TIME.MERCHANT,
    gcTime: GC_TIME.MERCHANT,
    enabled: !!id,
    ...options
  });
}

// Hook for fetching all organisations (for add organisation flow)
export function useAllOrganisations(
  params: Record<string, any> = {},
  options?: Omit<
    UseQueryOptions<{
      data: TransformedOrganisationsResponse['organisations'];
      pagination: TransformedOrganisationsResponse['pagination'];
    }>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: [...queryKeys.merchant.all, 'all-organisations', params],
    queryFn: async () => {
      const response = await merchantApiClient.getAddOrganisations(params);
      const transformed = getOrganisationsResponseTransformer(response as any);

      return {
        data: transformed.organisations,
        pagination: transformed.pagination
      };
    },
    staleTime: STALE_TIME.MERCHANT,
    gcTime: GC_TIME.MERCHANT,
    ...options
  });
}
